import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import { visuallyHidden } from "@mui/utils";

import { chipSx } from "@shared/utils/theme";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectIsEvaluator,
  selectIsCurrentUserWorkspaceOwner,
} from "@store/selectors";
import { useNotification } from "@shared/contexts/NotificationContext";
import {
  updateReadingStatus,
  updateCanvasStatus,
  updatePaperStatus,
} from "@services/api";
import {
  selectReading,
  selectCanvas,
  selectPaper,
} from "@store/thunks/workspaceThunks";
import { setBaseView, setPaperEditorMode } from "@store/slices/viewSlice";
import {
  getComparator,
  stableSort,
  parseDate,
} from "@features/documents/components/table-functions";
import type { Order } from "@features/documents/components/EnhancedTableHead";
import type { RootState } from "@store/index";
import type {
  WorkspaceResourceType,
  WorkspaceResourceView,
} from "@/types/workspace";
import {
  selectUnifiedResources,
  selectUnifiedResourcesLoading,
} from "../state/resourcesSelectors";
import {
  loadAllWorkspaceReadings,
  deleteReading,
  updateReading,
} from "@features/workspace-readings/state/workspaceReadingsSlice";
import {
  loadAllWorkspaceCanvases,
  deleteCanvas,
  renameCanvas,
} from "@features/workspace-canvases/state/workspaceCanvasesSlice";
import {
  loadAllWorkspacePapers,
  deletePaper,
  renamePaper,
} from "@features/workspace-papers/state/workspacePapersSlice";
import { CreateCanvasModal } from "@features/annogram/resources/components/CreateCanvasModal";
import { CreatePaperModal } from "@features/annogram/resources/components/CreatePaperModal";
import { CreateReadingModal } from "@features/workspace-readings/components/CreateReadingModal";
import AssignResourceModal from "./AssignResourceModal";
import GradesModal from "./GradesModal";
import { submissionLateness } from "../utils/lateness";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";

type SortKey = "name" | "type" | "assignedByUsername" | "createdAt" | "dueDate";

interface HeadCell {
  id: SortKey;
  label: string;
  numeric: boolean;
  evaluatorOnly?: boolean;
  // Owner/instructor-only. The creation date is housekeeping that's
  // meaningful when you own the resource but noise for a student
  // looking at assigned work.
  ownerOnly?: boolean;
}

const headCells: readonly HeadCell[] = [
  { id: "type", label: "Type", numeric: false },
  { id: "name", label: "Name", numeric: false },
  { id: "assignedByUsername", label: "Created by", numeric: false },
  { id: "createdAt", label: "Created", numeric: true, ownerOnly: true },
  { id: "dueDate", label: "Due date", numeric: true },
];

const typeMeta: Record<
  WorkspaceResourceType,
  { label: string; color: "primary" | "secondary" | "info"; icon: JSX.Element }
> = {
  reading: {
    label: "Reading",
    color: "primary",
    icon: <MenuBookOutlinedIcon fontSize="small" />,
  },
  canvas: {
    label: "Canvas",
    color: "secondary",
    icon: <AccountTreeOutlinedIcon fontSize="small" />,
  },
  paper: {
    label: "Paper",
    color: "info",
    icon: <DescriptionOutlinedIcon fontSize="small" />,
  },
};

// Statuses from which a student can still turn work in. Turn-in is
// additionally limited to graded resources (see the row action).
// 'returned' is included so a student can revise and re-submit.
const isTurnInnable = (status: string | null) =>
  status === "assigned" ||
  status === "in_progress" ||
  status === "returned";

// Student-facing label + chip hue for their own submission state, shown
// in the Grade column before a grade is released. Everything pre-turn-in
// (assigned / in_progress) reads as "Draft".
const studentStatusLabel = (status: string | null): string => {
  switch (status) {
    case "submitted":
    case "completed":
      return "Submitted";
    case "returned":
      return "Returned";
    default:
      return "Draft";
  }
};
const studentStatusHue = (status: string | null): string => {
  switch (status) {
    case "submitted":
    case "completed":
      return "submitted";
    case "returned":
      return "returned";
    default:
      return "draft";
  }
};

const assignmentKindLabel = (kind: string): string =>
  kind === "graded" ? "Graded" : kind === "required" ? "Required" : "Optional";

// Due dates carry a time of day (defaulting to end of day), so the Due
// column shows both — unlike Created, which is date-only (parseDate).
const formatDue = (iso: string | null): string =>
  iso
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(iso))
    : "";

type CreateKind = WorkspaceResourceType | null;

export default function UnifiedResourcesTable() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceId = useAppSelector(
    (state: RootState) => state.workspace.workspace_id
  );
  const rows = useAppSelector(selectUnifiedResources);
  const loading = useAppSelector(selectUnifiedResourcesLoading);
  const isEvaluator = useAppSelector(selectIsEvaluator);
  // Workspace owner = the instructor for this workspace. Student-facing
  // affordances (the Status column, the Turn-in action) are gated on
  // NOT being the owner — owners manage assignments, they don't turn in
  // their own work. (An owner naturally accrues a my_status row just by
  // opening a canvas, which is why role, not status, must gate this.)
  const isOwner = useAppSelector(selectIsCurrentUserWorkspaceOwner);
  const userId = useAppSelector((state: RootState) => state.user.userId);
  const { showSuccess, showError } = useNotification();
  const [turningInId, setTurningInId] = useState<string | null>(null);
  // Instructor "Assign" (due date + optional/required/graded). Gated to
  // owner+evaluator — owner is the security boundary (also enforced
  // server-side), evaluator is the paid-capability gate.
  const canManageAssignments = isOwner && isEvaluator;
  const [assignRows, setAssignRows] = useState<WorkspaceResourceView[] | null>(
    null
  );
  // Grades modal target (reading/canvas/paper — all gradable).
  const [gradesRow, setGradesRow] = useState<WorkspaceResourceView | null>(
    null
  );

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<SortKey>("createdAt");
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<WorkspaceResourceType | "all">(
    "all"
  );
  // "Assignments only" hides optional reference material, leaving the
  // required/graded assignments. For an instructor that's "just the
  // assignments"; for a student, "just my work".
  const [assignmentsOnly, setAssignmentsOnly] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [error, setError] = useState<string>("");
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [createKind, setCreateKind] = useState<CreateKind>(null);

  useEffect(() => {
    if (!workspaceId) return;
    dispatch(loadAllWorkspaceReadings(workspaceId));
    dispatch(loadAllWorkspaceCanvases(workspaceId));
    dispatch(loadAllWorkspacePapers(workspaceId));
  }, [workspaceId, dispatch]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (typeFilter !== "all" && row.type !== typeFilter) return false;
      if (assignmentsOnly && row.assignmentKind === "optional") return false;
      if (!term) return true;
      return (
        row.name.toLowerCase().includes(term) ||
        (row.assignedByUsername ?? "").toLowerCase().includes(term)
      );
    });
  }, [rows, search, typeFilter, assignmentsOnly]);

  const sortedRows = useMemo(() => {
    return stableSort(
      filteredRows as Array<WorkspaceResourceView & Record<SortKey, string | null>>,
      getComparator(order, orderBy)
    );
  }, [filteredRows, order, orderBy]);

  const pagedRows = useMemo(
    () =>
      sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const handleRequestSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(filteredRows.map((r) => r.id));
      return;
    }
    setSelected([]);
  };

  const handleRowClick = (_e: MouseEvent<unknown>, id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const goToWorkspace = () => navigate("/annogram");

  const handleView = async (row: WorkspaceResourceView) => {
    try {
      if (row.type === "reading") {
        await dispatch(setBaseView("reading"));
        await dispatch(selectReading(row.id));
      } else if (row.type === "canvas") {
        await dispatch(setBaseView("canvas"));
        await dispatch(selectCanvas(row.id));
      } else {
        await dispatch(setPaperEditorMode("modal"));
        await dispatch(selectPaper(row.id));
      }
      goToWorkspace();
    } catch (err) {
      console.error("Failed to open resource:", err);
      setError("Failed to open resource. Please try again.");
    }
  };

  const startEdit = (row: WorkspaceResourceView) => {
    setEditingId(row.id);
    setEditValue(row.name);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const commitEdit = async (row: WorkspaceResourceView) => {
    const next = editValue.trim();
    if (!next || next === row.name) {
      cancelEdit();
      return;
    }
    setSavingId(row.id);
    try {
      if (row.type === "reading") {
        await dispatch(
          updateReading({ readingId: row.id, data: { reading_name: next } })
        ).unwrap();
      } else if (row.type === "canvas") {
        await dispatch(
          renameCanvas({ canvasId: row.id, canvasName: next })
        ).unwrap();
      } else {
        await dispatch(
          renamePaper({ paperId: row.id, paperName: next })
        ).unwrap();
      }
    } catch (err) {
      console.error("Failed to rename resource:", err);
      setError("Failed to rename. Please try again.");
    } finally {
      setSavingId(null);
      cancelEdit();
    }
  };

  const handleEditKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    row: WorkspaceResourceView
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitEdit(row);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  const dispatchDelete = async (row: WorkspaceResourceView) => {
    if (row.type === "reading") {
      await dispatch(deleteReading(row.id)).unwrap();
    } else if (row.type === "canvas") {
      await dispatch(deleteCanvas(row.id)).unwrap();
    } else {
      await dispatch(deletePaper(row.id)).unwrap();
    }
  };

  const handleDelete = async (row: WorkspaceResourceView) => {
    if (
      !window.confirm(
        `Delete "${row.name || "Untitled"}"? This action cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingIds((ids) => [...ids, row.id]);
    try {
      await dispatchDelete(row);
      setSelected((prev) => prev.filter((x) => x !== row.id));
    } catch (err) {
      console.error("Failed to delete resource:", err);
      setError("Failed to delete. Please try again.");
    } finally {
      setDeletingIds((ids) => ids.filter((x) => x !== row.id));
    }
  };

  const handleTurnIn = async (row: WorkspaceResourceView) => {
    if (!userId) return;
    if (
      !window.confirm(
        `Turn in "${
          row.name || "Untitled"
        }"? Your instructor will see it as submitted.`
      )
    ) {
      return;
    }
    setTurningInId(row.id);
    try {
      const result =
        row.type === "reading"
          ? await updateReadingStatus(row.id, userId, "submitted")
          : row.type === "canvas"
            ? await updateCanvasStatus(workspaceId, row.id, userId, "submitted")
            : await updatePaperStatus(workspaceId, row.id, userId, "submitted");
      if (!result.success) {
        showError(result.error.message || "Could not turn in. Please try again.");
        return;
      }
      showSuccess("Turned in.");
      // Refetch the affected type so my_status reflects the new state.
      if (row.type === "reading") {
        dispatch(loadAllWorkspaceReadings(workspaceId));
      } else if (row.type === "canvas") {
        dispatch(loadAllWorkspaceCanvases(workspaceId));
      } else {
        dispatch(loadAllWorkspacePapers(workspaceId));
      }
    } catch (err) {
      console.error("Failed to turn in resource:", err);
      showError("Could not turn in. Please try again.");
    } finally {
      setTurningInId(null);
    }
  };

  const handleBulkDelete = async () => {
    const selectedRows = filteredRows.filter((r) => selected.includes(r.id));
    if (selectedRows.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedRows.length} resource${
          selectedRows.length === 1 ? "" : "s"
        }? This action cannot be undone.`
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    const results = await Promise.allSettled(
      selectedRows.map((r) => dispatchDelete(r))
    );
    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length > 0) {
      setError(
        `Failed to delete ${failures.length} of ${selectedRows.length} resources.`
      );
    }
    setSelected([]);
    setBulkDeleting(false);
  };

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length)
      : 0;

  const visibleHeadCells = headCells.filter(
    (h) => (!h.evaluatorOnly || isEvaluator) && (!h.ownerOnly || isOwner)
  );

  // Total column count for full-width rows (loading / empty / spacer):
  // visibleHeadCells + owner-only checkbox + Assignment + owner-only
  // Submissions + contributor-only Grade + Actions.
  const tableColSpan =
    visibleHeadCells.length +
    (isOwner ? 1 : 0) + // checkbox (owner)
    1 + // Assignment
    (isOwner ? 1 : 0) + // Submissions (owner)
    (!isOwner ? 1 : 0) + // Grade (contributor)
    1; // Actions

  // Selection-all UI matches what's visible to the user (i.e. filtered).
  const selectableCount = filteredRows.length;
  const numSelected = selected.length;

  const handleOpenCreateMenu = (e: MouseEvent<HTMLElement>) =>
    setCreateMenuAnchor(e.currentTarget);
  const handleCloseCreateMenu = () => setCreateMenuAnchor(null);
  const openCreate = (kind: WorkspaceResourceType) => {
    setCreateKind(kind);
    handleCloseCreateMenu();
  };
  const closeCreate = () => setCreateKind(null);

  return (
    <Box sx={{ width: "100%", p: 4 }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            gap: 2,
            flexWrap: "wrap",
            ...(numSelected > 0 && {
              bgcolor: (theme) =>
                alpha(
                  theme.palette.secondary.main,
                  theme.palette.action.activatedOpacity
                ),
            }),
          }}
        >
          {numSelected > 0 ? (
            <Typography variant="h6" sx={{ flex: "1 1 auto" }}>
              {numSelected} selected
            </Typography>
          ) : (
            <Typography
              variant="h6"
              id="resourcesTableTitle"
              component="div"
              sx={{ flex: "1 1 auto" }}
            >
              Resources
            </Typography>
          )}

          {numSelected > 0 ? (
            <Stack direction="row" spacing={0.5}>
              {canManageAssignments && (
                <Tooltip title="Assign selected">
                  <span>
                    <IconButton
                      aria-label={`Assign ${numSelected} selected`}
                      onClick={() =>
                        setAssignRows(
                          filteredRows.filter((r) => selected.includes(r.id))
                        )
                      }
                      disabled={bulkDeleting}
                    >
                      <EditCalendarOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Delete selected">
                <span>
                  <IconButton
                    aria-label={`Delete ${numSelected} selected`}
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DeleteIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : (
            <>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="resource-type-filter-label">Type</InputLabel>
                <Select
                  labelId="resource-type-filter-label"
                  label="Type"
                  value={typeFilter}
                  onChange={(e: SelectChangeEvent) => {
                    setTypeFilter(
                      e.target.value as WorkspaceResourceType | "all"
                    );
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="reading">Readings</MenuItem>
                  <MenuItem value="canvas">Canvases</MenuItem>
                  <MenuItem value="paper">Papers</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={assignmentsOnly}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setAssignmentsOnly(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="Assignments only"
              />
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: "28ch" }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateMenu}
              >
                Create
              </Button>
              <Menu
                anchorEl={createMenuAnchor}
                open={Boolean(createMenuAnchor)}
                onClose={handleCloseCreateMenu}
              >
                <MenuItem onClick={() => openCreate("reading")}>
                  <ListItemIcon>{typeMeta.reading.icon}</ListItemIcon>
                  <ListItemText>Reading</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => openCreate("canvas")}>
                  <ListItemIcon>{typeMeta.canvas.icon}</ListItemIcon>
                  <ListItemText>Canvas</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => openCreate("paper")}>
                  <ListItemIcon>{typeMeta.paper.icon}</ListItemIcon>
                  <ListItemText>Paper</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 600 }} size="medium">
            <TableHead>
              <TableRow>
                {/* Selection drives bulk actions (delete / assign), which
                    are owner-only — so the whole checkbox column is too. */}
                {isOwner && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        numSelected > 0 && numSelected < selectableCount
                      }
                      checked={
                        selectableCount > 0 && numSelected === selectableCount
                      }
                      onChange={handleSelectAllClick}
                      inputProps={{ "aria-label": "select all resources" }}
                    />
                  </TableCell>
                )}
                {visibleHeadCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? "right" : "left"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="center">Assignment</TableCell>
                {isOwner && (
                  <TableCell align="center">Submissions</TableCell>
                )}
                {!isOwner && <TableCell align="center">Grade</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tableColSpan}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tableColSpan}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No resources match your filters. Use *Create* to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row, idx) => {
                  const isItemSelected = selected.includes(row.id);
                  const isEditing = editingId === row.id;
                  const isSaving = savingId === row.id;
                  const isDeleting = deletingIds.includes(row.id);
                  // Overdue is status-aware: a student who's already
                  // submitted isn't overdue. Owners see the aggregate
                  // (past-due regardless of any one student's status).
                  const due = row.dueDate ? new Date(row.dueDate) : null;
                  const myDone =
                    row.myStatus === "submitted" ||
                    row.myStatus === "completed";
                  const isOverdue =
                    !!due && due < new Date() && (isOwner || !myDone);
                  const daysOverdue = isOverdue
                    ? Math.floor((Date.now() - due.getTime()) / 86400000)
                    : 0;
                  const labelId = `resources-row-${idx}`;
                  const meta = typeMeta[row.type];
                  return (
                    <TableRow
                      key={`${row.type}:${row.id}`}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      selected={isItemSelected}
                      tabIndex={-1}
                    >
                      {isOwner && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onClick={(e) => handleRowClick(e, row.id)}
                            inputProps={{ "aria-labelledby": labelId }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          icon={meta.icon}
                          label={meta.label}
                          size="small"
                          variant="outlined"
                          sx={chipSx(row.type)}
                        />
                      </TableCell>
                      <TableCell id={labelId}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, row)}
                            onBlur={() => commitEdit(row)}
                            autoFocus
                            disabled={isSaving}
                            inputProps={{ maxLength: 255 }}
                            fullWidth
                          />
                        ) : (
                          <>
                            <Typography
                              variant="body1"
                              fontWeight={500}
                              noWrap
                              title={row.name || undefined}
                              sx={{ maxWidth: { xs: 180, sm: 320, md: 420 } }}
                            >
                              {row.name || `Untitled ${meta.label}`}
                            </Typography>
                            {row.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                title={row.description}
                                sx={{ maxWidth: { xs: 180, sm: 320, md: 420 } }}
                              >
                                {row.description}
                              </Typography>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell>{row.assignedByUsername || "—"}</TableCell>
                      {isOwner && (
                        <TableCell align="right">
                          {parseDate(row.createdAt)}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {row.dueDate ? (
                          <Typography
                            variant="body2"
                            // Status-aware in the text itself. The overdue
                            // red is tuned per mode to clear WCAG AA on the
                            // paper background (MUI's default error red
                            // fails AA on both this theme's papers).
                            sx={(t) => ({
                              fontWeight: isOverdue ? 600 : 400,
                              color: isOverdue
                                ? t.palette.mode === "dark"
                                  ? "#ff8a80"
                                  : "#b71c1c"
                                : t.palette.text.primary,
                            })}
                          >
                            {formatDue(row.dueDate)}
                            {isOverdue &&
                              (daysOverdue >= 1
                                ? ` · ${daysOverdue}d overdue`
                                : " · overdue")}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={assignmentKindLabel(row.assignmentKind)}
                          size="small"
                          variant="outlined"
                          sx={chipSx(row.assignmentKind)}
                        />
                      </TableCell>
                      {isOwner && (
                        <TableCell align="center">
                          {row.assignmentKind === "optional" ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            <Tooltip
                              title={`${row.submittedCount} submitted · ${row.inProgressCount} in progress · ${row.notStartedCount} not started`}
                            >
                              <Typography variant="body2">
                                {row.submittedCount}/{row.assignedCount}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      {!isOwner && (
                        <TableCell align="center">
                          {row.assignmentKind !== "graded" ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : row.myPoints != null ? (
                            <Typography variant="body2" fontWeight={600}>
                              {row.myPoints}
                              {row.myMaxPoints != null
                                ? `/${row.myMaxPoints}`
                                : ""}
                            </Typography>
                          ) : (
                            // Not yet graded/released — show the student's
                            // own submission state instead of a generic
                            // "Pending": Draft → Submitted → Returned, plus
                            // on-time/late once submitted.
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              gap={0.25}
                            >
                              <Chip
                                label={studentStatusLabel(row.myStatus)}
                                size="small"
                                variant="outlined"
                                sx={chipSx(studentStatusHue(row.myStatus))}
                              />
                              {(() => {
                                if (
                                  row.myStatus !== "submitted" &&
                                  row.myStatus !== "completed"
                                )
                                  return null;
                                const l = submissionLateness(
                                  row.mySubmittedAt,
                                  row.dueDate
                                );
                                if (!l) return null;
                                return (
                                  <Typography
                                    variant="caption"
                                    sx={(t) => ({
                                      color: l.late
                                        ? t.palette.mode === "dark"
                                          ? "#ff8a80"
                                          : "#b71c1c"
                                        : t.palette.text.secondary,
                                    })}
                                  >
                                    {l.label}
                                  </Typography>
                                );
                              })()}
                            </Box>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Box
                          display="flex"
                          gap={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip title="Open">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Open ${row.name || "resource"}`}
                                onClick={() => handleView(row)}
                                disabled={isEditing}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canManageAssignments && (
                            <Tooltip title="Assign (due date / type)">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Edit assignment for ${
                                    row.name || "resource"
                                  }`}
                                  onClick={() => setAssignRows([row])}
                                  disabled={isEditing}
                                >
                                  <EditCalendarOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {canManageAssignments &&
                            row.assignmentKind === "graded" && (
                              <Tooltip title="Grades">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Grades for ${
                                      row.name || "resource"
                                    }`}
                                    onClick={() => setGradesRow(row)}
                                    disabled={isEditing}
                                  >
                                    <GradingOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          {!isOwner &&
                            row.assignmentKind === "graded" &&
                            isTurnInnable(row.myStatus) && (
                            <Tooltip title="Turn in">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  aria-label={`Turn in ${
                                    row.name || "resource"
                                  }`}
                                  onClick={() => handleTurnIn(row)}
                                  disabled={
                                    isEditing || turningInId === row.id
                                  }
                                >
                                  {turningInId === row.id ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <AssignmentTurnedInOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* Rename + Delete are owner-only management
                              actions (server-gated too). */}
                          {isOwner && (
                            <>
                              <Tooltip title="Rename">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Rename ${
                                      row.name || "resource"
                                    }`}
                                    onClick={() => startEdit(row)}
                                    disabled={isEditing}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Delete ${
                                      row.name || "resource"
                                    }`}
                                    onClick={() => handleDelete(row)}
                                    disabled={isDeleting || isEditing}
                                    sx={{
                                      color: "error.main",
                                      "&.Mui-disabled": {
                                        color: "error.light",
                                        opacity: 0.5,
                                      },
                                    }}
                                  >
                                    {isDeleting ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DeleteIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {emptyRows > 0 && pagedRows.length > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell
                    colSpan={tableColSpan}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <CreateReadingModal
        open={createKind === "reading"}
        onClose={closeCreate}
        workspaceId={workspaceId}
      />
      <CreateCanvasModal
        open={createKind === "canvas"}
        onClose={closeCreate}
        workspaceId={workspaceId}
      />
      <CreatePaperModal
        open={createKind === "paper"}
        onClose={closeCreate}
        workspaceId={workspaceId}
      />
      <AssignResourceModal
        open={assignRows !== null}
        rows={assignRows ?? []}
        workspaceId={workspaceId}
        onClose={() => setAssignRows(null)}
        onSaved={() => setSelected([])}
      />
      <GradesModal
        open={gradesRow !== null}
        resource={gradesRow}
        workspaceId={workspaceId}
        onClose={() => setGradesRow(null)}
        onSaved={() => {}}
      />
    </Box>
  );
}
